import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { isValidElement, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Button } from '../../button/Button';
import { Text } from '../../text';
import { AlertDialog } from './AlertDialog';
import { AlertDialogDescription } from './AlertDialogDescription';
import { AlertDialogFooter } from './AlertDialogFooter';
import { AlertDialogOverlay } from './AlertDialogOverlay';
import { AlertDialogPortal } from './AlertDialogPortal';
import { contentVariants } from './contentVariants';

/**
 * 確認ダイアログコンポーネント
 * ユーザーに操作の確認を求めるモーダルダイアログです。
 */
type Props = {
  /** ダイアログの開閉状態 */
  open: boolean;
  /** ダイアログの開閉状態を変更するコールバック */
  onOpenChange: (open: boolean) => void;
  /** 確認ボタンがクリックされたときのコールバック */
  onConfirm?: () => void;
  /** 確認ボタンのラベル（アイコン併記など JSX も可） */
  confirmLabel?: ReactNode;
  /** キャンセルボタンのラベル（デフォルト: 'キャンセル'） */
  cancelLabel?: ReactNode;
  /** キャンセルボタンを非表示にするかどうか */
  hideCancelButton?: boolean;
  /** 確認ボタンを非表示にするかどうか */
  hideConfirmButton?: boolean;
  /** ダイアログのタイトル */
  title?: ReactNode;
  /** ダイアログの本文コンテンツ */
  children: ReactNode;
  /** 確認ボタンのバリアント（default: 通常のアクション、destructive: 破壊的なアクション） */
  confirmVariant?: 'default' | 'destructive';
  /** ダイアログの幅（s: 480px, m: 640px, l: 1040px） */
  width?: 's' | 'm' | 'l';
  /** 確認ボタンのフォームID */
  confirmFormId?: string;
  /** 確認ボタンを無効化するかどうか */
  disabled?: boolean;
};

export const Dialog = ({
  open,
  onOpenChange,
  onConfirm,
  confirmLabel = '確認',
  cancelLabel = 'キャンセル',
  title,
  children,
  confirmVariant = 'default',
  hideCancelButton = false,
  hideConfirmButton = false,
  width = 'm',
  disabled = false,
  confirmFormId,
}: Props) => {
  // 不要なレンダリングやAPI通信を防ぐため
  if (!open) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPortal>
        <AlertDialogOverlay onClick={() => onOpenChange(false)} />
        <AlertDialogPrimitive.Content
          className={cn(contentVariants({ width }))}
          onEscapeKeyDown={() => onOpenChange(false)}
        >
          <AlertDialogPrimitive.Title className={cn(!title && 'sr-only')}>
            {!title ? null : isValidElement(title) ? title : <Text variant="subhead">{title}</Text>}
          </AlertDialogPrimitive.Title>
          <div className="space-y-4">
            <AlertDialogDescription>{children}</AlertDialogDescription>
          </div>
          <AlertDialogFooter className="gap-1">
            {!hideCancelButton && (
              <AlertDialogPrimitive.Cancel asChild>
                <Button variant="outline">{cancelLabel}</Button>
              </AlertDialogPrimitive.Cancel>
            )}
            {!hideConfirmButton &&
              (confirmFormId ? (
                // フォーム連携時はAlertDialogPrimitive.Actionを使わない
                // Actionはクリック時に自動でダイアログを閉じるため、バリデーション前に閉じてしまう
                <Button
                  type="submit"
                  form={confirmFormId}
                  variant={confirmVariant === 'destructive' ? 'destructive' : 'primary'}
                  disabled={disabled}
                  onClick={onConfirm}
                >
                  {confirmLabel}
                </Button>
              ) : (
                <AlertDialogPrimitive.Action asChild>
                  <Button
                    variant={confirmVariant === 'destructive' ? 'destructive' : 'primary'}
                    disabled={disabled}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </Button>
                </AlertDialogPrimitive.Action>
              ))}
          </AlertDialogFooter>
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    </AlertDialog>
  );
};
